package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.site.theme.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val placeholderWords = listOf(
    "SpongeBob",
    "Moana",
    "Diary of a Wimpy Kid",
    "Teen Titans",
    "MrBeast",
    "Minecraft",
    "Frozen",
    "Roblox"
)

private const val WORD_HEIGHT = 24
private const val CYCLE_DELAY_MS = 1000L

@Composable
fun SearchBar(
    query: String,
    onQueryChange: (String) -> Unit,
    onSearch: () -> Unit,
    onCancel: () -> Unit,
    isSearching: Boolean,
    showProgress: Boolean,
    filtersExpanded: Boolean,
    onToggleFilters: () -> Unit,
    onImagePicked: (ByteArray) -> Unit = {},
    onCancelRecognition: () -> Unit = {},
    isRecognizingText: Boolean = false,
    quickCheck: Boolean = false,
    suggestions: List<DisambiguationItem> = emptyList(),
    recentSearches: List<String> = emptyList(),
    isAuthenticated: Boolean = true,
    onSuggestionSelected: (DisambiguationItem) -> Unit = {},
    onRecentSelected: (String) -> Unit = {},
    onRemoveRecent: (String) -> Unit = {},
    /** Reports the box's own focus state — e.g. so the referral redeem popup's gate can wait for
     *  it to lose focus. Mirrors the mobile SearchBar's onFocusChanged. */
    onFocusChanged: (Boolean) -> Unit = {},
) {
    val isAnalyzing = isSearching || showProgress

    var currentIndex by remember { mutableStateOf(0) }
    var animate by remember { mutableStateOf(true) }
    var focused by remember { mutableStateOf(false) }
    var activeIndex by remember { mutableStateOf(-1) }
    // Keeps the local `focused` (dropdown visibility) and onFocusChanged (real caller-facing focus
    // state, e.g. for the referral redeem popup's gate) in lockstep. A suggestion/recent-search
    // click never fires the input's native blur (SearchDropdown's mousedown preventDefault keeps
    // real DOM focus in the input on purpose), so every place that hides the dropdown by setting
    // `focused = false` must also report that through onFocusChanged, or callers relying on it get
    // stuck thinking the box is still focused.
    fun setFocused(value: Boolean) {
        focused = value
        onFocusChanged(value)
    }

    // Show matches once the user has typed ≥2 chars; show recent searches on an empty focused box (signed-in only).
    val showSuggestions = focused && !isAnalyzing && query.length >= 2
    val showRecent = isAuthenticated && focused && !isAnalyzing && query.isEmpty() && recentSearches.isNotEmpty()

    // Cycle through words — seamless infinite loop
    LaunchedEffect(Unit) {
        while (true) {
            delay(CYCLE_DELAY_MS)
            animate = true
            currentIndex++
            if (currentIndex == placeholderWords.size) {
                // We scrolled to the duplicate first item; wait for animation then snap back
                delay(500)
                animate = false
                currentIndex = 0
            }
        }
    }

    // Container with icons inside - part of unified card (no background/shadow)
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(6.px)
            width(100.percent)
            property("position", "relative")
        }
    }) {
        if (isRecognizingText) {
            // "Reading your photo…" replaces the bar content while the extractor works; the
            // trailing ✕ lets the user bail out if it's taking too long.
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    width(100.percent)
                    gap(12.px)
                    padding(2.px, 4.px)
                }
            }) {
                Div(attrs = {
                    style {
                        width(20.px)
                        height(20.px)
                        marginLeft(12.px)
                        property("border", "2px solid #E6F7F6")
                        property("border-top-color", "#0E7C7B")
                        borderRadius(50.percent)
                        property("animation", "spin 0.8s linear infinite")
                        property("flex-shrink", "0")
                    }
                })
                Span(attrs = {
                    style {
                        color(Gray600); fontSize(16.px); fontWeight(500)
                        property("flex", "1")
                        property("animation", "breathe 0.9s ease-in-out infinite")
                    }
                }) {
                    Text("Reading your photo…")
                }
                Button(attrs = {
                    onClick { onCancelRecognition() }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        width(36.px)
                        height(36.px)
                        borderRadius(50.percent)
                        backgroundColor(Gray100)
                        property("border", "none")
                        cursor("pointer")
                        color(Color("#737373"))
                        fontSize(15.px)
                        fontWeight(700)
                        property("flex-shrink", "0")
                        property("transition", TransitionFast)
                    }
                    onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#E5E5E5" }
                    onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#F5F5F5" }
                }) { Text("✕") }
            }
        } else {
        // Settings plus button (left) — fades out when Quick Check is selected, space reserved
        Button(attrs = {
            id("cm-filters") // coachmark anchor (first-run tour)
            onClick { onToggleFilters() }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(48.px)
                height(48.px)
                borderRadius(50.percent)
                property("background", if (filtersExpanded) "linear-gradient(135deg, rgba(14, 124, 123, 0.15) 0%, rgba(20, 163, 160, 0.2) 100%)" else "transparent")
                property("border", "none")
                cursor("pointer")
                property("opacity", if (quickCheck) "0" else "1")
                property("pointer-events", if (quickCheck) "none" else "auto")
                property("transition", "background ${TransitionFast}, opacity 0.3s ease")
                property("position", "relative")
                property("flex-shrink", "0")
            }
        }) {
            Img(src = "/images/ic_plus.png", attrs = {
                style {
                    width(22.px)
                    height(22.px)
                    display(DisplayStyle.Block)
                    property("transform", if (filtersExpanded) "rotate(180deg)" else "rotate(0deg)")
                    property("transition", "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)")
                }
            })
        }

        // Input wrapper with animated placeholder
        Div(attrs = {
            style {
                property("flex", "1")
                property("position", "relative")
                property("min-width", "0")
            }
        }) {
            TextInput(value = query) {
                id("cm-search") // coachmark anchor (first-run tour)
                // Suppress the browser's native form-history / autofill dropdown (the dark
                // "tooltip" of past entries) — we render our own suggestions instead.
                attr("autocomplete", "off")
                attr("autocorrect", "off")
                attr("autocapitalize", "off")
                attr("spellcheck", "false")
                onInput {
                    onQueryChange(it.value)
                    setFocused(true)
                    activeIndex = -1
                }
                onFocus { setFocused(true) }
                onBlur { setFocused(false) }
                if (isAnalyzing) disabled()
                onKeyDown { ev ->
                    if (isAnalyzing) return@onKeyDown
                    when (ev.key) {
                        "Enter" -> {
                            val sel = if (showSuggestions) suggestions.getOrNull(activeIndex) else null
                            if (sel != null) {
                                ev.preventDefault()
                                setFocused(false)
                                onSuggestionSelected(sel)
                            } else {
                                onSearch()
                            }
                        }
                        "ArrowDown" -> if (showSuggestions && suggestions.isNotEmpty()) {
                            ev.preventDefault()
                            activeIndex = (activeIndex + 1).coerceAtMost(suggestions.size - 1)
                        }
                        "ArrowUp" -> if (showSuggestions && suggestions.isNotEmpty()) {
                            ev.preventDefault()
                            activeIndex = (activeIndex - 1).coerceAtLeast(-1)
                        }
                        "Escape" -> { setFocused(false); activeIndex = -1 }
                    }
                }
                style {
                    width(100.percent)
                    padding(12.px, 16.px)
                    property("border", "none")
                    fontSize(16.px)
                    property("outline", "none")
                    property("font-family", "inherit")
                    backgroundColor(Color.transparent)
                    property("box-sizing", "border-box")
                }
            }

            // Animated placeholder overlay
            if (query.isEmpty()) {
                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("top", "0")
                        property("left", "0")
                        property("right", "0")
                        property("bottom", "0")
                        padding(12.px, 16.px)
                        property("pointer-events", "none")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        color(Color("#A3A3A3"))
                        fontSize(16.px)
                        property("font-family", "inherit")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                    }
                }) {
                    Span { Text("Search \u00A0") }
                    // Scrolling word container
                    Span(attrs = {
                        style {
                            property("overflow", "hidden")
                            height(WORD_HEIGHT.px)
                            display(DisplayStyle.InlineBlock)
                            property("vertical-align", "bottom")
                        }
                    }) {
                        Div(attrs = {
                            style {
                                property("transform", "translateY(-${currentIndex * WORD_HEIGHT}px)")
                                property("transition", if (animate) "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)" else "none")
                            }
                        }) {
                            placeholderWords.forEach { word ->
                                Div(attrs = {
                                    style {
                                        height(WORD_HEIGHT.px)
                                        lineHeight(WORD_HEIGHT.px)
                                    }
                                }) { Text(word) }
                            }
                            // Duplicate first word for seamless loop
                            Div(attrs = {
                                style {
                                    height(WORD_HEIGHT.px)
                                    lineHeight(WORD_HEIGHT.px)
                                }
                            }) { Text(placeholderWords[0]) }
                        }
                    }
                }
            }
        }

        // Camera button for OCR
        if (!isAnalyzing) {
            ImagePickerButton(
                onImagePicked = onImagePicked,
                enabled = !isRecognizingText,
                isLoading = isRecognizingText
            )
        }

        // Search/Cancel button (right). Two-tap during analysis to prevent accidental cancels —
        // see Compose counterpart (SearchBar.kt) for rationale.
        if (isAnalyzing) {
            var cancelArmed by remember { mutableStateOf(false) }
            LaunchedEffect(cancelArmed) {
                if (cancelArmed) {
                    kotlinx.coroutines.delay(3000)
                    cancelArmed = false
                }
            }
            LaunchedEffect(isAnalyzing) {
                if (!isAnalyzing) cancelArmed = false
            }
            Button(attrs = {
                onClick {
                    if (cancelArmed) {
                        cancelArmed = false
                        onCancel()
                    } else {
                        cancelArmed = true
                    }
                }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    height(48.px)
                    padding(0.px, 24.px)
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    property("background", if (cancelArmed) "#B91C1C" else "#6B7280")
                    color(Color.white)
                    property("border", "none")
                    fontSize(15.px)
                    fontWeight(700)
                    cursor("pointer")
                    property("font-family", "inherit")
                    property("transition", TransitionMedium)
                    gap(6.px)
                    property("flex-shrink", "0")
                }
            }) {
                Text(if (cancelArmed) "Tap again" else "Cancel")
            }
        } else {
            Button(attrs = {
                onClick { onSearch() }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    property("background", "transparent")
                    property("border", "none")
                    cursor("pointer")
                    property("transition", TransitionFast)
                    property("flex-shrink", "0")
                }
            }) {
                Img(src = "/images/ic_search.png", attrs = {
                    style {
                        width(22.px)
                        height(22.px)
                        display(DisplayStyle.Block)
                    }
                })
            }
        }

        // Autocomplete dropdown — absolute so it overlays content below without triggering the
        // persistent card's height animation (which would clip it via overflow:hidden).
        if (showSuggestions || showRecent) {
            SearchDropdown(
                query = query,
                suggestions = if (showSuggestions) suggestions else emptyList(),
                recentSearches = if (showRecent) recentSearches else emptyList(),
                activeIndex = activeIndex,
                onSuggestionSelected = { setFocused(false); activeIndex = -1; onSuggestionSelected(it) },
                onRecentSelected = { setFocused(false); onRecentSelected(it) },
                onRemoveRecent = onRemoveRecent,
                onSearchAnyway = { setFocused(false); onSearch() }
            )
        }
        } // end else (!isRecognizingText)
    }

    // Inject animations
    InjectSpinKeyframes()
    InjectStyles(
        "search-bar-styles",
        """
        @keyframes breathe {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
        }
        """.trimIndent()
    )
}

/** Short media-type label for autocomplete rows (mirrors DisambiguationList). */
private fun suggestionTypeLabel(type: String?): String = when (type?.lowercase()) {
    "movie", "movies" -> "Movie"
    "tv_show", "tv_shows", "tv show", "tv series" -> "TV Show"
    "game", "games", "video game", "video_game" -> "Game"
    "youtube", "youtube channel" -> "YouTube"
    "book", "books" -> "Book"
    else -> "Media"
}

@Composable
private fun DropdownHeader(text: String) {
    Div(attrs = {
        style {
            padding(10.px, 18.px, 6.px)
            fontSize(12.px)
            fontWeight(700)
            color(Gray500)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.04em")
        }
    }) { Text(text) }
}

/**
 * The autocomplete menu rendered under the search input. Shows either recent searches (empty box)
 * or ranked title suggestions plus a trailing "search anyway" row so a miss is never a dead end.
 * A container-level mousedown preventDefault keeps focus in the input so onBlur doesn't close the
 * menu before a click lands.
 */
@Composable
private fun SearchDropdown(
    query: String,
    suggestions: List<DisambiguationItem>,
    recentSearches: List<String>,
    activeIndex: Int,
    onSuggestionSelected: (DisambiguationItem) -> Unit,
    onRecentSelected: (String) -> Unit,
    onRemoveRecent: (String) -> Unit = {},
    onSearchAnyway: () -> Unit
) {
    Div(attrs = {
        onMouseDown { it.preventDefault() }
        style {
            property("position", "absolute")
            property("top", "100%")
            property("left", "0")
            property("right", "0")
            property("z-index", "50")
            marginTop(8.px)
            backgroundColor(Color.white)
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            border(1.px, LineStyle.Solid, Gray200)
            property("box-shadow", "0 8px 24px rgba(0,0,0,0.12)")
            property("overflow", "hidden")
            property("max-height", "360px")
            property("overflow-y", "auto")
        }
    }) {
        if (recentSearches.isNotEmpty()) {
            DropdownHeader("Recent searches")
            recentSearches.forEach { q ->
                Div(attrs = {
                    onClick { onRecentSelected(q) }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("padding", "12px 8px 12px 18px")
                        cursor("pointer")
                        property("transition", TransitionFast)
                    }
                    onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#F0FAF9" }
                    onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#FFFFFF" }
                }) {
                    Span(attrs = { style { fontSize(15.px); color(Gray900); property("flex", "1") } }) { Text(q) }
                    Span(attrs = {
                        onMouseDown { it.stopPropagation(); it.preventDefault(); onRemoveRecent(q) }
                        style {
                            property("flex-shrink", "0")
                            width(32.px); height(32.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            borderRadius(50.percent)
                            color(Color("#9CA3AF"))
                            fontSize(14.px)
                            fontWeight(600)
                            cursor("pointer")
                        }
                        onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.color = "#6B7280" }
                        onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.color = "#9CA3AF" }
                    }) { Text("✕") }
                }
            }
        } else {
            suggestions.forEachIndexed { index, item ->
                val active = index == activeIndex
                Div(attrs = {
                    onClick { onSuggestionSelected(item) }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                        padding(12.px, 18.px)
                        cursor("pointer")
                        backgroundColor(if (active) Color("#F0FAF9") else Color.white)
                        property("transition", TransitionFast)
                    }
                    onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#F0FAF9" }
                    onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = if (active) "#F0FAF9" else "#FFFFFF" }
                }) {
                    Div(attrs = { style { property("min-width", "0"); property("flex", "1") } }) {
                        Div(attrs = {
                            style {
                                fontSize(15.px); fontWeight(600); color(Gray900)
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }) { Text(item.title) }
                        val subtitle = listOfNotNull(item.year, item.info).filter { it.isNotBlank() }.joinToString(" • ")
                        if (subtitle.isNotEmpty()) {
                            Div(attrs = { style { fontSize(13.px); color(Gray500); marginTop(2.px) } }) { Text(subtitle) }
                        }
                    }
                    Div(attrs = {
                        style {
                            property("background", "linear-gradient(135deg, rgba(14,124,123,0.08) 0%, rgba(20,163,160,0.08) 100%)")
                            borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                            padding(5.px, 12.px)
                            fontSize(12.px)
                            fontWeight(600)
                            color(Primary)
                            property("white-space", "nowrap")
                            property("flex-shrink", "0")
                            property("order", "-1")   // render the type badge leftmost
                        }
                    }) { Text(suggestionTypeLabel(item.mediaType)) }
                }
            }
            // Fallback — hands a miss off to the full search pipeline.
            Div(attrs = {
                onClick { onSearchAnyway() }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    padding(13.px, 18.px)
                    cursor("pointer")
                    property("border-top", "1px solid #F0F0F0")
                    color(Primary)
                    fontSize(14.px)
                    fontWeight(600)
                    property("transition", TransitionFast)
                }
                onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#F0FAF9" }
                onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "#FFFFFF" }
            }) {
                Span(attrs = { style { fontSize(14.px) } }) { Text("🔍") }
                Text(if (query.isNotBlank()) "Search “$query”" else "Search")
            }
        }
    }
}
